/**
 * Promo Scratch Game — front-end behaviour.
 *
 * Three performance-minded, opt-in enhancements, all gated on
 * prefers-reduced-motion and progressive enhancement. Headline letters: each
 * letter pops in elastically on first view (shared letters-reveal component),
 * optionally looping. Background photo parallax:
 * the football photo drifts a few percent as the block passes through the
 * viewport, delegated to the shared front-end component (js/front/components/parallax).
 * Ticket entrance: the two tickets slide up and rotate into place the first time
 * the block scrolls into view — the pre-animation (hidden) state is only armed by
 * JS, so with no JS or reduced motion the tickets stay fully visible.
 */

import { initImageParallax } from '../../js/front/components/parallax';
import { initLettersReveal } from '../../js/front/letters-reveal';

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Arm and trigger the tickets entrance animation once, on first view.
 *
 * @param {HTMLElement} block The block root element.
 */
const initTickets = ( block ) => {

	const tickets = block.querySelector( '.b-promo-scratch-game__tickets' );

	if ( ! tickets ) {

		return;

	}

	// No IntersectionObserver or reduced motion: leave the tickets in their
	// default (fully visible) state and skip the animation entirely.
	if ( prefersReducedMotion() || ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	tickets.classList.add( 'is-armed' );

	/**
	 * Reveal when the block enters view. The observer is only wired up after the
	 * armed (hidden) state has had a frame to paint — otherwise a block that is
	 * already in the viewport on load would gain "is-visible" in the same paint
	 * as "is-armed", collapsing the transition so the tickets simply snap in.
	 */
	const reveal = () => {

		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						tickets.classList.add( 'is-visible' );
						observer.disconnect();

					}

				} );

			},
			{ threshold: 0.25 },
		);

		observer.observe( block );

	};

	/**
	 * Set the tickets' position based on their size.
	 *
	 * @return {void}
	 */
	const SetTicketsPosition = () => {

		const parent = block.querySelector( '.b-promo-scratch-game__tickets' );
		const child = parent?.querySelector( '.b-promo-scratch-game__ticket--1' );

		if ( ! parent || ! child ) {

			return;

		}

		/**
		 * Check if the child element fits within the parent element.
		 * @return {void}
		 */
		const checkSize = () => {

			const parentHeight = parent.clientHeight;
			const childHeight = ( child.offsetHeight + 80 );

			parent.classList.toggle(
				'has-contained-child',
				childHeight <= parentHeight
			);

		};

		const observer = new ResizeObserver( checkSize );

		observer.observe( parent );

	}



	requestAnimationFrame( () => requestAnimationFrame( () => {

		reveal();
		SetTicketsPosition();

	} ) );

};

document.querySelectorAll( '.b-promo-scratch-game' ).forEach( ( block ) => {

	if ( block.classList.contains( 'has-parallax' ) ) {

		initImageParallax( block, { imageSelector: '.b-promo-scratch-game__image' } );

	}

	initTickets( block );

	// Headline letters pop in (moving-letters #9) on first view; the editor's
	// loop toggle keeps replaying it. Missing title (no headline) is a no-op.
	initLettersReveal( block.querySelector( '.b-promo-scratch-game__title' ), {
		wrapWords: true,
		loop: block.classList.contains( 'has-title-loop' ),
	} );

} );
